import {match} from 'ts-pattern';

import {useMemo} from 'react';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {
  Key,
  Option,
  OptionWithProperties,
  VehicleCatalogueData,
  VehicleCatalogueReturnType,
} from '../types/VehicleCatalogueLogic';
import {sortByLabel} from '../utils/sortByLabel';

interface UseVehicleCatalogueLogicProps {
  data?: VehicleCatalogueData;
  type?: string | Nullish;
  isError?: boolean;
  isLoading?: boolean;
  shouldSortByLabel?: boolean;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function useVehicleCatalogueLogic(
  props: UseVehicleCatalogueLogicProps
): VehicleCatalogueReturnType {
  const {data, isError, isLoading, type, shouldSortByLabel = true} = props;
  const vehicleType = isNotNilOrEmpty(type) ? type! : 'VEHICLETYPE_PASSENGER_CAR';

  const catalogueData = useMemo(() => makeVehicleCatalogueByVehicleType(data), [data]);

  // eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
  const vehicleUtils = useMemo(() => {
    const vehicleTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'vehicle_type',
      catalogueData
    );

    const driveOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'drive',
      catalogueData
    );

    const fuelTypeOptions: OptionWithProperties[] | Nullish =
      getOptionsWithPropertiesByVehicleTypeAndProperty(
        vehicleType,
        'fuel_type',
        catalogueData
      )?.reduce((options: OptionWithProperties[], option) => {
        if (option.value === 'FUELTYPE_PETROL' || option.value === 'FUELTYPE_DIESEL') {
          return [option, ...options];
        }
        return [...options, option];
      }, []);

    const hybridTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'hybrid_type',
      catalogueData
    );

    const batteryTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'battery_type',
      catalogueData
    );

    const chargingConnectorTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'charging_connector_type',
      catalogueData
    );

    const batteryOwnershipTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'battery_ownership_type',
      catalogueData
    );

    const handDriveOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'hand_drive_type',
      catalogueData
    );

    const vehicleStyleOptionsUnsorted: Option[] | Nullish = match(vehicleType)
      .with('VEHICLETYPE_PASSENGER_CAR', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'car_style', catalogueData)
      )
      .with('VEHICLETYPE_BUS', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'bus_style', catalogueData)
      )
      .with('VEHICLETYPE_CARAVAN', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'caravan_style', catalogueData)
      )
      .with('VEHICLETYPE_MOTORCYCLE', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'motorcycle_style', catalogueData)
      )
      .with('VEHICLETYPE_TRUCK', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'truck_style', catalogueData)
      )
      .with('VEHICLETYPE_SEMI_TRUCK', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'semi_truck_style', catalogueData)
      )
      .with('VEHICLETYPE_VAN', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'van_style', catalogueData)
      )
      .with('VEHICLETYPE_SEMI_TRAILER', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'semi_trailer_style', catalogueData)
      )
      .with('VEHICLETYPE_TRAILER', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'trailer_style', catalogueData)
      )
      .with('VEHICLETYPE_CONSTRUCTION_MACHINE', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'construction_machine_style', catalogueData)
      )
      .with('VEHICLETYPE_AGRICULTURAL_MACHINE', () =>
        getOptionsByVehicleTypeAndProperty(vehicleType, 'agriculture_machine_style', catalogueData)
      )
      .otherwise(() => getOptionsByVehicleTypeAndProperty(vehicleType, 'car_style', catalogueData));

    const vehicleStyleOptions = shouldSortByLabel
      ? sortByLabel(vehicleStyleOptionsUnsorted)
      : vehicleStyleOptionsUnsorted;

    const transmissionOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'transmission',
      catalogueData
    );

    const bodyColorTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'color_type',
      catalogueData
    );

    const bodyColorOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'body_color',
      catalogueData
    );

    const doorCountOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'door_count',
      catalogueData
    )?.filter((option) => option.label.includes('/'));

    const seatCountOptions: Option[] | Nullish = Array.from({length: 9}).map((_, index) => ({
      label: (index + 1).toString(),
      value: (index + 1).toString(),
    }));

    const interiorColorOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'interior_color',
      catalogueData
    );

    const fullDoorCountOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'door_count',
      catalogueData
    );

    const interiorMaterialOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'interior_material',
      catalogueData
    );

    const serviceBookTypeOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'service_book_type',
      catalogueData
    );

    const serviceBookStateOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'service_book_state',
      catalogueData
    );

    const vehicleConditionOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'condition',
      catalogueData
    );

    const emissionClassOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'emission_class',
      catalogueData
    );

    const dimensionsHeightOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'dimensions_height',
      catalogueData
    );

    const dimensionsLengthOptions: Option[] | Nullish = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'dimensions_length',
      catalogueData
    );

    const featureOptions = getOptionsByVehicleTypeAndProperty(
      vehicleType,
      'feature',
      catalogueData
    );

    const getVehicleType = (style: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'vehicle_type', style, catalogueData);

    const getBodyColor = (bodyColor: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'body_color', bodyColor, catalogueData);

    const getBodyColorHEX = (bodyColor: string | Nullish) => {
      const bodyColors = data?.find((item) => item.property_name === 'body_color')?.keys ?? [];
      const bodyColorObject = bodyColors.find((key) => key.const_key === bodyColor);

      return bodyColorObject?.properties
        ?.find((property) => property?.name === 'color_code')
        ?.value?.toString();
    };

    const getBatteryType = (batteryType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'battery_type',
        batteryType,
        catalogueData
      );

    const getChargingConnectorType = (chargingConnectorType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'charging_connector_type',
        chargingConnectorType,
        catalogueData
      );

    const getBatteryOwnershipType = (batteryOwnershipType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'battery_ownership_type',
        batteryOwnershipType,
        catalogueData
      );

    const getColorType = (colorType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'color_type', colorType, catalogueData);

    const getInteriorMaterial = (interiorMaterial: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'interior_material',
        interiorMaterial,
        catalogueData
      );

    const getEmissionClass = (emissionClass: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'emission_class',
        emissionClass,
        catalogueData
      );

    const getHandDrive = (handDriveType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'hand_drive_type',
        handDriveType,
        catalogueData
      );

    const getDoorCount = (doorCount: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'door_count', doorCount, catalogueData);

    const getFuelType = (fuelType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'fuel_type', fuelType, catalogueData);

    const getHybridType = (hybridType: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'hybrid_type',
        hybridType,
        catalogueData
      );

    const getBodyStyle = (carStyle: string | Nullish) =>
      match(vehicleType)
        .with('VEHICLETYPE_PASSENGER_CAR', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'car_style',
            carStyle,
            catalogueData
          )
        )
        .with('VEHICLETYPE_BUS', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'bus_style',
            carStyle,
            catalogueData
          )
        )
        .with('VEHICLETYPE_CARAVAN', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'caravan_style',
            carStyle,
            catalogueData
          )
        )
        .with('VEHICLETYPE_MOTORCYCLE', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'motorcycle_style',
            carStyle,
            catalogueData
          )
        )
        .with('VEHICLETYPE_TRUCK', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'truck_style',
            carStyle,
            catalogueData
          )
        )
        .with('VEHICLETYPE_SEMI_TRUCK', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'semi_truck_style',
            carStyle,
            catalogueData
          )
        )
        .with('VEHICLETYPE_VAN', () =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'van_style',
            carStyle,
            catalogueData
          )
        )
        .otherwise(() =>
          getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'car_style',
            carStyle,
            catalogueData
          )
        );

    const getDrive = (drive: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'drive', drive, catalogueData);

    const getFeature = (feature: string | Nullish) =>
      // Alpha return interior material as a feature
      feature?.startsWith('INTERIORMATERIAL_')
        ? getPropertyLabelByVehicleTypeAndProperty(
            vehicleType,
            'interior_material',
            feature,
            catalogueData
          )
        : getPropertyLabelByVehicleTypeAndProperty(vehicleType, 'feature', feature, catalogueData);

    const getTransmission = (transmission: string | Nullish) =>
      getPropertyLabelByVehicleTypeAndProperty(
        vehicleType,
        'transmission',
        transmission,
        catalogueData
      );

    return {
      getVehicleType,
      getBodyStyle,
      getFuelType,
      getHybridType,
      getBatteryType,
      getChargingConnectorType,
      getBatteryOwnershipType,
      getHandDrive,
      getDrive,
      getFeature,
      getTransmission,
      getColorType,
      getBodyColor,
      getBodyColorHEX,
      getInteriorMaterial,
      getEmissionClass,
      getDoorCount,
      handDriveOptions,
      driveOptions,
      fuelTypeOptions,
      hybridTypeOptions,
      vehicleStyleOptions,
      batteryTypeOptions,
      batteryOwnershipTypeOptions,
      chargingConnectorTypeOptions,
      transmissionOptions,
      bodyColorTypeOptions,
      bodyColorOptions,
      doorCountOptions,
      seatCountOptions,
      interiorColorOptions,
      fullDoorCountOptions,
      interiorMaterialOptions,
      vehicleTypeOptions,
      featureOptions,
      serviceBookStateOptions,
      serviceBookTypeOptions,
      vehicleConditionOptions,
      emissionClassOptions,
      dimensionsHeightOptions,
      dimensionsLengthOptions,
    };
  }, [catalogueData, vehicleType, shouldSortByLabel, data]);

  return [vehicleUtils, {isError: isError ?? false, isLoading: isLoading ?? false}];
}

type CatalogueByVehicleType = {
  [vehicleType: string]: {
    [propertyName: string]: Key[];
  };
};

export function makeVehicleCatalogueByVehicleType(catalogue?: VehicleCatalogueData) {
  return catalogue?.reduce((catalogueData: CatalogueByVehicleType, catalogueItem) => {
    if (!catalogueItem.vehicle_type || !catalogueItem.property_name) {
      return catalogueData;
    }
    if (catalogueItem.vehicle_type in catalogueData) {
      return {
        ...catalogueData,
        [catalogueItem.vehicle_type]: {
          ...catalogueData[catalogueItem.vehicle_type],
          [catalogueItem.property_name]: catalogueItem.keys.map((key, index) => ({
            ...key,
            sort_priority: index,
          })),
        },
      };
    }
    return {
      ...catalogueData,
      [catalogueItem.vehicle_type]: {
        [catalogueItem.property_name]: catalogueItem.keys.map((key, index) => ({
          ...key,
          sort_priority: index,
        })),
      },
    };
  }, {});
}

export function getOptionsByVehicleTypeAndProperty(
  vehicleType: string,
  property: string,
  catalogue: CatalogueByVehicleType | Nullish
) {
  return catalogue?.[vehicleType]?.[property]
    ?.sort((a, b) => (a.sort_priority ?? 0) - (b.sort_priority ?? 0))
    .map((item) => ({
      label: item.labels[0]?.label ?? item.const_key,
      value: item.const_key,
    }));
}

export function getOptionsWithPropertiesByVehicleTypeAndProperty(
  vehicleType: string,
  property: string,
  catalogue: CatalogueByVehicleType | Nullish
) {
  return catalogue?.[vehicleType]?.[property]
    ?.sort((a, b) => (a.sort_priority ?? 0) - (b.sort_priority ?? 0))
    .map((item) => ({
      label: item.labels[0]?.label ?? item.const_key,
      value: item.const_key,
      properties: item.properties
        ?.map((property) => {
          if (property?.name && property?.value) {
            return {
              label: property.name,
              value: property.value,
            };
          }

          return null;
        })
        ?.filter(isNotNil),
    }));
}

export function getPropertyLabelByVehicleTypeAndProperty(
  vehicleType: string,
  property: string,
  key: string | Nullish,
  catalogue: CatalogueByVehicleType | Nullish
) {
  return (
    catalogue?.[vehicleType]?.[property]?.find((e) => e.const_key === key)?.labels[0]?.label ?? key
  );
}
